import { useEffect, useRef, useState } from 'react';
import { logger } from '@/lib/utils/logger';
import type { AutoHeightIframeProps } from './AutoHeightIframe.types';

export default function AutoHeightIframe({
  title,
  srcDoc,
  className = '',
  ...props
}: AutoHeightIframeProps) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const [height, setHeight] = useState<string>('0px');

  useEffect(() => {
    const iframe = iframeRef.current;
    if (!iframe) return;

    const updateHeight = () => {
      try {
        const doc = iframe.contentDocument;
        if (!doc) return;

        // Use requestAnimationFrame to ensure layout is settled
        requestAnimationFrame(() => {
          const body = doc.body;
          const html = doc.documentElement;

          // Use scrollHeight which gives accurate content height
          const calculatedHeight = Math.max(body.scrollHeight, html.scrollHeight);

          if (calculatedHeight > 0) {
            setHeight(`${calculatedHeight}px`);
          }
        });
      } catch (err) {
        logger.warn('Failed to access iframe content:', err);
      }
    };

    const handleLoad = () => {
      const doc = iframe.contentDocument;
      if (!doc) return;

      // Cleanup previous observers if they exist
      if (iframe._cleanup) {
        iframe._cleanup();
      }

      // Inject CSS to hide internal scrollbars
      // eslint-disable-next-line @typescript-eslint/no-deprecated
      const style = doc.createElement('style');
      style.textContent = `
        html, body {
          overflow: hidden !important;
          margin: 0;
          padding: 0;
        }
      `;
      doc.head.appendChild(style);

      // Watch internal DOM changes
      const mutationObserver = new MutationObserver(updateHeight);
      mutationObserver.observe(doc.documentElement, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
      });

      // Watch image loads
      Array.from(doc.images).forEach((img) => img.addEventListener('load', updateHeight));

      // Watch font loading changes
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      if (doc.fonts) {
        void doc.fonts.ready.then(updateHeight);
      }

      // Initial measurement after setup
      updateHeight();

      // Cleanup
      iframe._cleanup = () => {
        mutationObserver.disconnect();
        Array.from(doc.images).forEach((img) => img.removeEventListener('load', updateHeight));
      };
    };

    iframe.addEventListener('load', handleLoad);

    return () => {
      iframe.removeEventListener('load', handleLoad);
      if (iframe._cleanup) iframe._cleanup();
    };
  }, []); // Only run on mount

  return (
    <>
      <iframe
        ref={iframeRef}
        title={title}
        srcDoc={srcDoc}
        sandbox="allow-same-origin"
        className={`w-full border-0 block ${className}`}
        {...props}
        style={{
          height,
        }}
      />
    </>
  );
}
